import { IconFile, IconFileTypePdf, IconPhoto, IconTrash, IconExternalLink } from '@tabler/icons-react';
import type { MedicalRecordLocal } from '../../types/medicalRecords';
import { isImageType, isPdfType, getFileTypeLabel, CATEGORY_BADGE_LABELS, CATEGORY_BADGE_COLORS } from '../../types/medicalRecords';

interface RecordCardProps {
  record: MedicalRecordLocal;
  onDelete: () => void;
  disabled?: boolean;
}

// Format date for display
function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Get icon based on content type
function getFileIcon(contentType: string) {
  if (isPdfType(contentType)) {
    return IconFileTypePdf;
  }
  if (isImageType(contentType)) {
    return IconPhoto;
  }
  return IconFile;
}

export function RecordCard({ record, onDelete, disabled = false }: RecordCardProps) {
  const FileIcon = getFileIcon(record.contentType);
  const typeLabel = getFileTypeLabel(record.contentType);

  const handleView = () => {
    if (record.url) {
      window.open(record.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="card p-4 group">
      <div className="flex items-center gap-4">
        {/* File icon */}
        <div className="w-12 h-12 rounded-lg bg-cyan-900/30 border border-cyan-500/30 flex items-center justify-center flex-shrink-0">
          <FileIcon size={24} className="text-cyan-400" />
        </div>

        {/* File info */}
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-200 truncate" title={record.filename}>
            {record.filename}
          </h4>
          <div className="flex items-center gap-2 text-sm text-gray-500 mt-0.5">
            <span>{typeLabel}</span>
            <span className="text-gray-700">•</span>
            <span>Uploaded {formatDate(record.uploadedAt)}</span>
            {record.documentCategory && (
              <>
                <span className="text-gray-700">•</span>
                <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium border ${CATEGORY_BADGE_COLORS[record.documentCategory]}`}>
                  {CATEGORY_BADGE_LABELS[record.documentCategory]}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={handleView}
            disabled={disabled || !record.url}
            className="p-2 rounded-lg text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors disabled:opacity-50"
            title="View file"
          >
            <IconExternalLink size={18} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={disabled}
            className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
            title="Delete file"
          >
            <IconTrash size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
